<template>
  <div :class="$style.box">
    <SectionTitle :class="$style.box__title">引受目安検索</SectionTitle>
    <div :class="$style.box__content">
      <div v-if="searchText.length > 0" :class="$style.result">
        <div :class="$style.search">
          <p :class="$style.search__label">検索ワード</p>
          <ul :class="$style.search__words">
            <li>{{ searchText }}</li>
          </ul>
        </div>
        <DocumentList
          v-if="!loading"
          :corporations="filteredCorporations"
          @click="clickDocument"
        />

        <LoadingIndicator v-else />

        <DocumentModal
          :isOpened="isModalOpened"
          :fileUrl="fileUrl"
          @close="closeModal"
        />
      </div>
      <div v-else :class="$style.box__empty">
        <OkanekoInsurance :class="$style.box__emptyImg" viewBox="0 0 800 800" />
        <p>
          左の検索ボックスに病名を入力すると、各社の引受目安を参照できます。
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import OkanekoInsurance from '@/assets/images/okaneko-insurance.svg?component'

import {
  useSearchCoverageLazyQuery,
  type SearchCoverageQuery,
  useSearchCoverageGetCoverageViewDocumentFileUrlLazyQuery,
} from './CoverageSearchView.generated'
import DocumentList, {
  type Corporation,
  type Document,
} from './DocumentList.vue'
import DocumentModal from './DocumentModal.vue'
import { computed, ref, watch } from 'vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  searchText: string
}>()

const fileUrl = ref<string | null>(null)
const isModalOpened = ref<boolean>(false)

const closeModal = () => {
  fileUrl.value = null
  isModalOpened.value = false
}

const logger = useLogger()

const {
  load: loadSearchCoverage,
  result,
  loading,
} = useSearchCoverageLazyQuery({
  text: '',
})

const search = (text: string) => {
  loadSearchCoverage(undefined, { text })
  logger.ltlPeople.event.searchSolicitorCloudCoverage(logger.buildMeta(), {
    data: {
      searchText: text,
    },
  })
}

watch(
  () => props.searchText,
  value => {
    if (value.length > 0) {
      search(value)
    }
  },
  { immediate: true }
)

const { load: loadFile, onResult } =
  useSearchCoverageGetCoverageViewDocumentFileUrlLazyQuery({
    ids: [],
  })

const page = ref<Maybe<number>>(undefined)

onResult(result => {
  const url =
    result.data.solicitorCloudCoverageDocumentVersionByIds?.[0].fileUrl ?? null

  fileUrl.value = `${url}#page=${page.value}`
})

const clickDocument = async ({
  documentVersionId: id,
  page: _page,
}: {
  documentVersionId: SearchCoverageQuery['searchSolicitorCloudCoverages'][number]['documentVersion']['id']
  page: number
}) => {
  isModalOpened.value = true
  page.value = _page

  loadFile(
    undefined,
    {
      ids: [id],
    },
    {
      fetchPolicy: 'no-cache',
    }
  )

  logger.ltlPeople.event.clickSolicitorCloudDocument(logger.buildMeta(), {
    data: { id, type: 'coverage-document' },
  })
}

const filteredCorporations = computed<Corporation[]>((): Corporation[] => {
  if (!result.value) {
    return []
  }

  const grouped = result.value.searchSolicitorCloudCoverages.reduce<{
    [key in string]: SearchCoverageQuery['searchSolicitorCloudCoverages']
  }>((accumulator, result) => {
    const corporationID = result.documentVersion.document.corporation.id
    return {
      ...accumulator,
      [corporationID]: [...(accumulator[corporationID] || []), result],
    }
  }, {})

  return Object.values(grouped).map((results): Corporation => {
    const corporation = results[0].documentVersion.document.corporation

    return {
      ...corporation,
      documents: results.map((result): Document => {
        const documentVersion = result.documentVersion

        return {
          documentVersionId: documentVersion.id,
          title: `${result.fullText} ${result.pageNumber}ページ`,
          page: result.pageNumber,
        }
      }),
    }
  })
})
</script>

<style lang="scss" module>
.box {
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  background-color: $color-surface-primary;

  &__content {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid $color-border-low-emphasis;
  }

  &__empty {
    background-color: $color-surface-secondary;
    border-radius: 10px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & > *:nth-child(n + 2) {
      margin-top: 20px;
    }
  }

  &__emptyImg {
    $size: 200px;

    width: $size;
    height: $size;
  }
}

.result {
  & > *:nth-child(n + 2) {
    margin-top: 20px;
  }
}

.search {
  display: flex;
  align-items: center;

  &__label {
    font-weight: bold;
    font-size: 16px;
  }

  &__words {
    display: flex;
    align-items: center;
    margin-left: 10px;

    li {
      background-color: $color-surface-secondary;
      border-radius: 15px;
      padding: 4px 10px;
      font-size: 12px;

      &:nth-child(n + 2) {
        margin-left: 5px;
      }
    }
  }
}
</style>
